import { v4 as uuidv4 } from 'uuid';
import { MAX_PROMPTS_PER_PROFILE } from '@/constants/EditMaxes.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { EditLimitReachedError } from '@/errors/edit/EditLimitReachedError.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { ConfigNotFoundError } from '@/errors/edit/ConfigNotFoundError.ts';
import { NameInUseError } from '@/errors/edit/NameInUseError.ts';
import { ValidationFailedError } from '@/errors/edit/ValidationFailedError.ts';
import type { VariableConfig } from '@/types/edit/VariableConfig.ts';
import { VariableService } from '@/services/VariableService.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { DEFAULT_AI_MODEL } from '@/constants/DefaultAiModel.ts';
import { usePromptStore } from '@/stores/PromptStore.ts';
import { DefaultPromptConfigs } from '@/constants/DefaultPromptConfigs.ts';
import { VariableConfigService } from '@/services/config/VariableConfigService.ts';
import {
    getDefaultReasoningEffort,
    normalizeReasoningEffort,
    resolveClientModelId,
} from '@/utils/ModelCompatibilityUtils.ts';

export abstract class PromptConfigService {
    public static createPrompt(
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        profileConfigId: ProfileConfig['id'],
        stepConfigId: StepConfig['id'],
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
        stepConfigs: Record<StepConfig['id'], StepConfig>;
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
        newPromptConfigId: PromptConfig['id'];
    } {
        const profilePrompts = Object.values(promptConfigs).filter(
            (p) => p.profileId === profileConfigId && p.stepId === stepConfigId,
        );

        if (profilePrompts.length >= MAX_PROMPTS_PER_PROFILE) {
            throw new EditLimitReachedError('Max prompts reached');
        }

        const newPromptConfigId = uuidv4();

        const sessionStore = useSessionStore();
        const modelId = sessionStore.defaultModelId || DEFAULT_AI_MODEL;

        promptConfigs = {
            ...promptConfigs,
            [newPromptConfigId]: {
                id: newPromptConfigId,
                profileId: profileConfigId,
                stepId: stepConfigId,
                name: 'Enter Prompt Name',
                rawText:
                    'Enter your prompt here. You can include variables using the $ symbol. Click the variables button to view all available variables.',
                outputFormatInstruction: '',
                order: profilePrompts.length,
                formatAs: 'markdown',
                includeScreenCapture: false,
                modelId,
                reasoningEffort: getDefaultReasoningEffort(sessionStore.aiModels[modelId]),
                editable: true,
            },
        };

        // Update profile config
        profileConfigs = {
            ...profileConfigs,
            [profileConfigId]: {
                ...profileConfigs[profileConfigId],
                promptIds: [...profileConfigs[profileConfigId].promptIds, newPromptConfigId],
            },
        };

        // Update step config
        stepConfigs = {
            ...stepConfigs,
            [stepConfigId]: {
                ...stepConfigs[stepConfigId],
                promptIds: [...stepConfigs[stepConfigId].promptIds, newPromptConfigId],
            },
        };

        return {
            profileConfigs,
            stepConfigs,
            promptConfigs,
            newPromptConfigId,
        };
    }

    public static duplicatePrompt(
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
        stepConfigs: Record<StepConfig['id'], StepConfig>;
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
        newPromptConfigId: PromptConfig['id'];
    } {
        const promptConfigToDuplicate = promptConfigs[promptConfigId];
        if (!promptConfigToDuplicate) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        const profilePrompts = Object.values(promptConfigs).filter(
            (p) =>
                p.profileId === promptConfigToDuplicate.profileId &&
                p.stepId === promptConfigToDuplicate.stepId,
        );

        if (profilePrompts.length >= MAX_PROMPTS_PER_PROFILE) {
            throw new EditLimitReachedError('Max prompts reached');
        }

        const newPromptConfigId = uuidv4();

        promptConfigs = {
            ...promptConfigs,
            [newPromptConfigId]: {
                ...promptConfigToDuplicate,
                id: newPromptConfigId,
                name: `${promptConfigToDuplicate.name} (Copy)`,
            },
        };

        // Update profile config
        profileConfigs = {
            ...profileConfigs,
            [promptConfigToDuplicate.profileId]: {
                ...profileConfigs[promptConfigToDuplicate.profileId],
                promptIds: [
                    ...profileConfigs[promptConfigToDuplicate.profileId].promptIds,
                    newPromptConfigId,
                ],
            },
        };

        // Update step config
        stepConfigs = {
            ...stepConfigs,
            [promptConfigToDuplicate.stepId]: {
                ...stepConfigs[promptConfigToDuplicate.stepId],
                promptIds: [
                    ...stepConfigs[promptConfigToDuplicate.stepId].promptIds,
                    newPromptConfigId,
                ],
            },
        };

        return {
            profileConfigs,
            stepConfigs,
            promptConfigs,
            newPromptConfigId,
        };
    }

    public static getAllPromptConfigs = (): Record<PromptConfig['id'], PromptConfig> => {
        const promptStore = usePromptStore();
        const sessionStore = useSessionStore();
        const runtimeDefaultPromptConfigs = Object.fromEntries(
            Object.entries(DefaultPromptConfigs).map(([id, prompt]) => {
                const modelId = resolveClientModelId(
                    prompt,
                    sessionStore.aiModels,
                    sessionStore.defaultModelId,
                    sessionStore.modelReplacements,
                );

                return [id, modelId ? { ...prompt, modelId } : prompt];
            }),
        );

        return {
            ...runtimeDefaultPromptConfigs,
            ...promptStore.promptConfigs,
        };
    };

    public static getCustomPromptConfigs = (): Record<PromptConfig['id'], PromptConfig> => {
        const promptStore = usePromptStore();

        return promptStore.promptConfigs;
    };

    public static getPromptConfig = (id: PromptConfig['id']): PromptConfig => {
        return this.getAllPromptConfigs()[id];
    };

    public static updatePromptName(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
        name: PromptConfig['name'],
    ): {
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
    } {
        const promptConfig = promptConfigs[promptConfigId];
        if (!promptConfig) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        if (name.length === 0 || name.length > 50) {
            throw new ValidationFailedError('Prompt name must be between 1 and 50 characters long');
        }

        if (
            Object.values(promptConfigs).find(
                (p) =>
                    p.profileId === promptConfig.profileId &&
                    p.name.toLowerCase() === name.toLowerCase() &&
                    p.id !== promptConfigId,
            )
        ) {
            throw new NameInUseError(
                `The prompt name ${name} is already being used by another prompt in this profile.`,
            );
        }

        promptConfigs = {
            ...promptConfigs,
            [promptConfigId]: {
                ...promptConfig,
                name: name,
            },
        };

        return {
            promptConfigs,
        };
    }

    public static updatePromptRawText(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
        rawText: PromptConfig['rawText'],
    ): {
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
    } {
        const promptConfig = promptConfigs[promptConfigId];
        if (!promptConfig) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        promptConfigs = {
            ...promptConfigs,
            [promptConfigId]: {
                ...promptConfig,
                rawText: rawText,
            },
        };

        return {
            promptConfigs,
        };
    }

    public static updatePromptFormat(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
        format: PromptConfig['formatAs'],
    ): {
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
    } {
        const promptConfig = promptConfigs[promptConfigId];
        if (!promptConfig) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        promptConfigs = {
            ...promptConfigs,
            [promptConfigId]: {
                ...promptConfig,
                formatAs: format,
            },
        };

        return {
            promptConfigs,
        };
    }

    public static updatePromptModelId(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
        modelId: PromptConfig['modelId'],
    ): {
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
    } {
        const promptConfig = promptConfigs[promptConfigId];
        if (!promptConfig) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        promptConfigs = {
            ...promptConfigs,
            [promptConfigId]: {
                ...promptConfig,
                modelId: modelId,
                reasoningEffort: normalizeReasoningEffort(
                    useSessionStore().aiModels[modelId],
                    promptConfig.reasoningEffort,
                ),
            },
        };

        return {
            promptConfigs,
        };
    }

    public static updatePromptOutputFormatInstruction(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
        outputFormatInstruction: PromptConfig['outputFormatInstruction'],
    ) {
        const promptConfig = promptConfigs[promptConfigId];
        if (!promptConfig) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        promptConfigs = {
            ...promptConfigs,
            [promptConfigId]: {
                ...promptConfig,
                outputFormatInstruction: outputFormatInstruction,
            },
        };

        return {
            promptConfigs,
        };
    }

    public static updatePromptIncludeScreenCapture(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
        includeScreenCapture: PromptConfig['includeScreenCapture'],
    ) {
        const promptConfig = promptConfigs[promptConfigId];
        if (!promptConfig) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        promptConfigs = {
            ...promptConfigs,
            [promptConfigId]: {
                ...promptConfig,
                includeScreenCapture: includeScreenCapture,
            },
        };

        return {
            promptConfigs,
        };
    }

    public static updatePromptReasoningEffort(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
        reasoningEffort: PromptConfig['reasoningEffort'],
    ) {
        const promptConfig = promptConfigs[promptConfigId];
        if (!promptConfig) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        promptConfigs = {
            ...promptConfigs,
            [promptConfigId]: {
                ...promptConfig,
                reasoningEffort,
            },
        };

        return {
            promptConfigs,
        };
    }

    public static movePrompt(
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        stepConfigId: StepConfig['id'],
        promptConfigId: PromptConfig['id'],
        direction: 'up' | 'down',
    ): {
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
    } {
        const stepConfig = stepConfigs[stepConfigId];
        if (!stepConfig) {
            throw new Error(`StepConfig with id ${stepConfigId} not found`);
        }

        const stepPromptConfigs = Object.fromEntries(
            Object.entries(promptConfigs).filter(([promptConfigId]) =>
                stepConfig.promptIds.includes(promptConfigId),
            ),
        );

        const { promptConfigs: updatePromptConfigs } = this.calculateNewPromptsOrder(
            stepPromptConfigs,
            promptConfigId,
            direction,
        );

        const updatedPromptConfigs = {
            ...promptConfigs,
            ...updatePromptConfigs,
        };

        return {
            promptConfigs: updatedPromptConfigs,
        };
    }

    public static updatePromptConfigs(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
    ): void {
        const filteredConfigs = Object.fromEntries(
            Object.entries(promptConfigs).filter(([, config]) => config.editable),
        );
        const promptStore = usePromptStore();
        promptStore.setPromptConfigs(filteredConfigs);
    }

    public static deletePrompt(
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
        stepConfigs: Record<StepConfig['id'], StepConfig>;
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
    } {
        const promptConfig = promptConfigs[promptConfigId];
        if (!promptConfig) {
            throw new Error(`PromptConfig with id ${promptConfigId} not found`);
        }

        const stepId = promptConfig.stepId;

        // Remove the prompt from promptConfigs
        const { [promptConfigId]: _, ...unorderedUpdatedPromptConfigs } = promptConfigs;

        // Identify and reorder only those prompt configs that belong to the same step
        const promptsToReorder = Object.values(unorderedUpdatedPromptConfigs)
            .filter((config) => config.stepId === stepId)
            .sort((a, b) => a.order - b.order)
            .map((config, index) => ({
                ...config,
                order: index,
            }));

        // Leave prompt configs for other steps unchanged
        const promptsNotReordered = Object.values(unorderedUpdatedPromptConfigs).filter(
            (config) => config.stepId !== stepId,
        );

        // Merge the two sets
        const newPromptConfigsArray = [...promptsToReorder, ...promptsNotReordered];

        const updatedPromptConfigs = Object.fromEntries(
            newPromptConfigsArray.map((config) => [config.id, config]),
        );

        // Remove prompt from step config
        const updatedStepConfigs = {
            ...stepConfigs,
            [stepId]: {
                ...stepConfigs[stepId],
                promptIds: stepConfigs[stepId].promptIds.filter((id) => id !== promptConfigId),
            },
        };

        // Remove prompt from profile config
        const updatedProfileConfigs = {
            ...profileConfigs,
            [promptConfig.profileId]: {
                ...profileConfigs[promptConfig.profileId],
                promptIds: profileConfigs[promptConfig.profileId].promptIds.filter(
                    (id) => id !== promptConfigId,
                ),
            },
        };

        return {
            profileConfigs: updatedProfileConfigs,
            stepConfigs: updatedStepConfigs,
            promptConfigs: updatedPromptConfigs,
        };
    }

    public static validate(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        variableConfigs: Record<VariableConfig['id'], VariableConfig>,
    ): string[] {
        const errors: string[] = [];

        const sessionStore = useSessionStore();

        for (const promptConfig of Object.values(promptConfigs)) {
            // Name
            if (promptConfig.name.length === 0) {
                errors.push(`[Prompt "${promptConfig.name}"] Prompt name cannot be empty`);
            } else if (promptConfig.name.length > 50) {
                errors.push(
                    `[Prompt "${promptConfig.name}"] Prompt name cannot be longer than 50 characters`,
                );
            }

            // Variable definitions
            const readOnlyVariableConfigs = VariableConfigService.getVariableConfigs(
                promptConfigs,
                promptConfig.profileId,
                ['DEFAULT', 'PROMPT_OUTPUT', 'PROMPT_USER_INPUT'],
            );
            const allVariableConfigs = {
                ...variableConfigs,
                ...readOnlyVariableConfigs,
            };

            const possibleVariableConfigKeys = Object.values(allVariableConfigs).map((v) => v.key);

            const extractedVariables = VariableService.extractVariableKeys(promptConfig.rawText);

            for (const extractedVariable of extractedVariables) {
                if (!possibleVariableConfigKeys.includes(extractedVariable)) {
                    errors.push(
                        `[Prompt "${promptConfig.name}"] Variable ${extractedVariable} is not defined in this profile`,
                    );
                }
            }

            // AI model support
            const aiModel = sessionStore.aiModels[promptConfig.modelId];
            if (aiModel) {
                if (promptConfig.includeScreenCapture && !aiModel.supportsImage) {
                    errors.push(
                        `[Prompt "${promptConfig.name}"] Selected AI model does not support image input which is required when attaching screen capture to the output.`,
                    );
                }
                if (promptConfig.formatAs === 'choice' && !aiModel.supportsStructuredOutput) {
                    errors.push(
                        `[Prompt "${promptConfig.name}"] Selected AI model does not support structured output which is required for choice prompts.`,
                    );
                }
                if (
                    promptConfig.reasoningEffort !== undefined &&
                    !aiModel.supportedReasoningEfforts.includes(promptConfig.reasoningEffort)
                ) {
                    errors.push(
                        `[Prompt "${promptConfig.name}"] Selected AI model does not support reasoning effort "${promptConfig.reasoningEffort}".`,
                    );
                }
            }
        }

        return errors;
    }

    private static calculateNewPromptsOrder(
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        promptConfigId: PromptConfig['id'],
        direction: 'up' | 'down',
    ): { promptConfigs: Record<PromptConfig['id'], PromptConfig> } {
        const promptConfigToUpdate = promptConfigs[promptConfigId];
        if (!promptConfigToUpdate) {
            throw new ConfigNotFoundError(`Prompt config with id ${promptConfigId} not found`);
        }

        const keys = Object.keys(promptConfigs);
        const order = promptConfigToUpdate.order;
        let swapIndex = -1;

        if (direction === 'up' && order > 0) {
            swapIndex = order - 1;
        } else if (direction === 'down' && order < keys.length - 1) {
            swapIndex = order + 1;
        }

        if (swapIndex !== -1) {
            const configToSwap = Object.values(promptConfigs).find((p) => p.order === swapIndex);
            if (!configToSwap) {
                return {
                    promptConfigs: promptConfigs,
                };
            }

            const tempOrder = promptConfigToUpdate.order;
            promptConfigToUpdate.order = configToSwap.order;
            configToSwap.order = tempOrder;
        }

        const updatedPromptConfigs = Object.fromEntries(
            Object.entries(promptConfigs).sort(([, a], [, b]) => a.order - b.order),
        );

        return {
            promptConfigs: updatedPromptConfigs,
        };
    }
}
